import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, ParamMap, Params, Router, RouterLink } from '@angular/router';
import { distinctUntilChanged, map, Observable, switchMap } from 'rxjs';
import { ReportExportFormat, ReportsState, ReportsStore } from './reports.store';
import { parseRange, ReportRange, sameRange } from '../../../utils/report-range';
import { plural } from '../../../utils/plural';
import { formatNumber } from '../../../utils/format-amount';

@Component({
  selector: 'reports-page',
  templateUrl: './reports.page.html',
  styleUrl: './reports.page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, RouterLink]
})
export class ReportsPage implements OnInit, OnDestroy {
  readonly state$: Observable<ReportsState>;

  formatNumber = formatNumber;

  constructor(
    private store: ReportsStore,
    private route: ActivatedRoute,
    private router: Router,
    private destroyRef: DestroyRef
  ) {
    this.state$ = this.store.state$;
  }

  ngOnInit(): void {
    // The URL is the single source of truth for the range: the inputs write it and this one
    // stream reads it. switchMap drops the in-flight request when a newer range arrives
    this.route.queryParamMap
      .pipe(
        map(params => {
          const range = parseRange(params);

          this.normalizeUrl(params, range);

          return range;
        }),
        // the normalized URL parses to the same range, no second request
        distinctUntilChanged(sameRange),
        switchMap(range => this.store.load(range)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  // A cleared input writes nothing, and the default comes back through the URL
  onFromChange(from: string): void {
    this.navigate({ from: from || null });
  }

  onToChange(to: string): void {
    this.navigate({ to: to || null });
  }

  // rows, not Zgłoś actions: one action across three zlecenia is three of them
  rowCountLabel(count: number): string {
    return `${count} ${plural(count, 'wiersz', 'wiersze', 'wierszy')}`;
  }

  onFormatChange(format: string): void {
    this.store.setFormat(format as ReportExportFormat);
  }

  // Arkusz Excel is built by the API in the background and lands in the shell's tray (0106, 0107),
  // CSV in the browser from the rows on screen (0105); the store branches on the picked format
  export(): void {
    this.store.export();
  }

  // A bare or stale link (no parameters, a bad day, od after do) is rewritten to the range it
  // shows, so the address always says what is on screen
  private normalizeUrl(params: ParamMap, range: ReportRange): void {
    if (params.get('from') !== range.from || params.get('to') !== range.to) {
      this.navigate(range);
    }
  }

  // Every range gesture replaces the history entry: the page is one entry, whatever days
  // were picked, so Wstecz always leaves it
  private navigate(queryParams: Params): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  ngOnDestroy(): void {
    this.store.destroy();
  }
}
